import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reportService } from '@services';
import { NL2SQLConfig, NL2SQLMetadata, NL2SQLChartType } from '@app-types';

export const useReportWidgets = (enabled: boolean = true) => {
  const queryClient = useQueryClient();

  // Fetch widgets
  const {
    data: widgetsData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['reportWidgets'],
    queryFn: () => reportService.getReportWidgets(),
    enabled
  });

  const widgets = widgetsData?.widgets || [];

  // Create widget mutation - uses query_id, SQL is secured on server
  const createWidgetMutation = useMutation({
    mutationFn: (widget: {
      name: string;
      query_id: string;
      visualization_type: NL2SQLChartType;
      config?: NL2SQLConfig;
      metadata?: NL2SQLMetadata;
    }) => reportService.createReportWidget(widget),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportWidgets'] });
    }
  });

  // Delete widget mutation
  const deleteWidgetMutation = useMutation({
    mutationFn: (widgetId: number) => reportService.deleteReportWidget(widgetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportWidgets'] });
    }
  });

  // Helper functions
  const createWidget = async (widget: Parameters<typeof createWidgetMutation.mutate>[0]) => {
    return createWidgetMutation.mutate(widget);
  };

  const deleteWidget = (widgetId: number) => {
    if (window.confirm('Are you sure you want to delete this widget?')) {
      deleteWidgetMutation.mutate(widgetId);
    }
  };

  return {
    widgets,
    isLoading,
    error,
    createWidget,
    deleteWidget,
    isCreating: createWidgetMutation.isPending,
    isDeleting: deleteWidgetMutation.isPending
  };
};
